import { useModalState } from '@vtex/admin-ui'
import { useNavigation } from '@vtex/raccoon-next'
import { useFeature } from 'hooks'
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react'
import { Event, SimplifiedPromotion } from 'typings'
import { useAnalytics } from 'use-analytics'
import { DuplicateModal } from './Modal'

type Props = {
  eventSource: 'action-menu' | 'promotion-list'
}

export const DuplicateModalWithFlags = forwardRef<DuplicateModalRef, Props>(
  (props: Props, ref) => {
    const { eventSource } = props
    const itemRef = useRef<SimplifiedPromotion | null>(null)
    const { track } = useAnalytics()

    const modalState = useModalState()
    const { getFeature } = useFeature()
    const { navigate } = useNavigation()

    const duplicateModalFlag = getFeature('duplicate_modal')

    const onDuplicate = useCallback(
      (item: SimplifiedPromotion) => {
        itemRef.current = item
        if (duplicateModalFlag) {
          modalState.show()
          return
        }
        duplicate()
      },

      // eslint-disable-next-line react-hooks/exhaustive-deps
      [modalState]
    )

    useImperativeHandle(
      ref,
      () => ({
        onDuplicate,
      }),
      [onDuplicate]
    )

    const duplicate = () => {
      track(Event.DUPLICATED_PROMOTION, {
        id: itemRef.current?.id,
        type: itemRef.current?.type,
        status: itemRef.current?.status,
        archived: false,
        source: eventSource,
      })

      navigate(
        `/new?duplicate=${itemRef.current?.id}&type=${itemRef.current?.type}`
      )
    }

    return (
      duplicateModalFlag && (
        <DuplicateModal onConfirm={duplicate} state={modalState} />
      )
    )
  }
)

DuplicateModalWithFlags.displayName = 'DuplicateModalWithFlags'

export interface DuplicateModalRef {
  onDuplicate: (item: SimplifiedPromotion) => void
}
